import type { GoalProgress } from '@budget/shared';

const SIZE = 72;
const STROKE = 6;
const RADIUS = (SIZE - STROKE) / 2;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

interface Props {
  progress: GoalProgress;
  /** Décrit le pôle pour les technologies d'assistance. */
  label: string;
}

/** Position sur l'anneau, en partant du haut plutôt que de trois heures. */
function pointAt(ratio: number, distance: number): [number, number] {
  const angle = ratio * 2 * Math.PI - Math.PI / 2;
  return [SIZE / 2 + distance * Math.cos(angle), SIZE / 2 + distance * Math.sin(angle)];
}

const STATUS_WORDS: Record<GoalProgress['status'], string> = {
  reached: 'objectif atteint',
  ahead: 'en avance',
  'on-track': 'à l’heure',
  behind: 'en retard',
  open: 'sans échéance',
};

/**
 * Anneau de progression d'un pôle.
 *
 * Ce n'est PAS un camembert : on ne compare pas des parts entre elles, chaque
 * anneau porte son propre objectif. Le pourcentage est écrit au centre — c'est
 * lui qui informe, l'arc n'est qu'un ancrage visuel.
 *
 * Le cran marque la trajectoire attendue à date. Sans lui, 48 % ne dit pas si
 * c'est une bonne ou une mauvaise nouvelle.
 */
export function GoalRing({ progress, label }: Props) {
  const ratio = progress.progressRatio;
  const filled = Math.min(1, Math.max(0, ratio ?? 0));

  const expectedRatio =
    progress.expectedCents !== null && progress.targetAmountCents !== null
      ? Math.min(1, Math.max(0, progress.expectedCents / progress.targetAmountCents))
      : null;

  const [tickInnerX, tickInnerY] =
    expectedRatio === null ? [0, 0] : pointAt(expectedRatio, RADIUS - STROKE / 2 - 1);
  const [tickOuterX, tickOuterY] =
    expectedRatio === null ? [0, 0] : pointAt(expectedRatio, RADIUS + STROKE / 2 + 1);

  return (
    <svg
      viewBox={`0 0 ${SIZE} ${SIZE}`}
      className="h-auto w-full"
      role="img"
      aria-label={
        ratio === null
          ? `${label} : ${STATUS_WORDS[progress.status]}`
          : `${label} : ${Math.round(ratio * 100)} %, ${STATUS_WORDS[progress.status]}`
      }
    >
      <circle
        cx={SIZE / 2}
        cy={SIZE / 2}
        r={RADIUS}
        fill="none"
        stroke="var(--series-track)"
        strokeWidth={STROKE}
      />

      {ratio !== null && filled > 0 && (
        <circle
          cx={SIZE / 2}
          cy={SIZE / 2}
          r={RADIUS}
          fill="none"
          stroke="var(--series)"
          strokeWidth={STROKE}
          strokeLinecap="round"
          strokeDasharray={`${filled * CIRCUMFERENCE} ${CIRCUMFERENCE}`}
          transform={`rotate(-90 ${SIZE / 2} ${SIZE / 2})`}
        />
      )}

      {expectedRatio !== null && (
        <line
          x1={tickInnerX}
          y1={tickInnerY}
          x2={tickOuterX}
          y2={tickOuterY}
          stroke="var(--ink)"
          strokeWidth="2"
          strokeLinecap="round"
          opacity="0.5"
        />
      )}

      <text
        x={SIZE / 2}
        y={SIZE / 2}
        textAnchor="middle"
        dominantBaseline="central"
        className="fill-ink"
        fontSize="17"
        fontWeight="600"
      >
        {ratio === null ? '—' : `${Math.round(ratio * 100)}%`}
      </text>
    </svg>
  );
}
